import { AfterViewInit, Component, ElementRef, inject, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartWrapperService, ChartWrapperInstance } from '@shared-lib/shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import {
  SelfServiceChartOptionBuilder,
  ChartStyle,
  ChartShapeOptions,
  UploadedDataset,
  DatasetColumn,
} from '@shared-lib/utils/self-service-chart-option.builder';
import {
  filterSelfServiceRows,
  SelfServiceWidgetMeta,
  SelfServiceFilters,
} from '@shared-lib/utils/self-service-data.utils';

interface SelfServiceChartMeta {
  chartType: string;
  /** previewKind resolved by the builder component — the published view does
   *  NOT re-derive it, so a new chart type needs no change here. */
  previewKind: string;
  mapping: Record<string, string>;
  aggregation: string;
  seriesField: string;
  titleText: string;
  style: ChartStyle;
  shapeOptions: ChartShapeOptions;
  customAggregations: Array<{ key: string; label: string; expression: string }>;
  /** Resolved palette colours for style.palette — carried so no palette table
   *  is duplicated here. */
  resolvedPalette: string[];
  /** Builtin aggregation values — used by the builder to tell builtin vs custom. */
  builtinAggValues: string[];
}

/**
 * SelfServiceEChartsComponent
 *
 * Renders a published self-service ECharts chart so it is IDENTICAL to the
 * builder preview. It does this by rebuilding the option with the very same
 * SelfServiceChartOptionBuilder the builder preview uses — there is one source
 * of truth for every chart type, so there is no per-type "patching" that can
 * drift or miss a shape.
 *
 * On every global-filter / cross-filter change it re-narrows the raw rows
 * (filterSelfServiceRows) and rebuilds the option from scratch, keeping the
 * published view fully reactive while still pixel-matching the preview.
 */
@Component({
  selector: 'app-self-service-echarts',
  standalone: true,
  imports: [],
  template: `<div #chartHost class="ss-echarts-host" [id]="data?.widgetId"></div>`,
  styles: [`
    .ss-echarts-host { width: 100%; height: 100%; min-height: 0; }
    :host { display: block; width: 100%; height: 100%; }
  `],
})
export class SelfServiceEChartsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  @ViewChild('chartHost', { static: true }) chartHost?: ElementRef<HTMLDivElement>;

  private chart?: echarts.ECharts;
  private wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private globalFilterService = inject(GlobalFilterService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;
  private allCategories: string[] = [];
  private builder?: SelfServiceChartOptionBuilder;

  ngAfterViewInit(): void {
    this.configureBuilder();

    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.rebuild(filters),
      getChart: () => this.chart as echarts.ECharts,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
    });

    this.rebuild(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.chart?.resize());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].firstChange || !this.chart) return;
    this.updateData(this.data);
  }

  updateData(data: any): void {
    this.data = data;
    this.configureBuilder();
    this.rebuild(this.globalFilterService.getCurrentFilters());
  }

  private configureBuilder(): void {
    const meta = this.chartMeta();
    if (!meta) return;
    this.builder = new SelfServiceChartOptionBuilder({
      palettes: [{ key: meta.style?.palette || 'default', label: 'meta', colors: meta.resolvedPalette || [] }],
      customAggregations: meta.customAggregations || [],
      builtinAggregations: (meta.builtinAggValues || []).map((value) => ({ label: value, value })),
      chartTypes: [{ value: meta.chartType, previewKind: meta.previewKind }],
    });
  }

  private chartMeta(): SelfServiceChartMeta | undefined {
    return this.data?.ssChartMeta as SelfServiceChartMeta | undefined;
  }

  private ensureChart(): echarts.ECharts | undefined {
    const host = this.chartHost?.nativeElement;
    if (!host) return undefined;
    if (!this.chart || this.chart.getDom() !== host) {
      this.chart?.dispose();
      this.chart = echarts.init(host);
    }
    return this.chart;
  }

  /** Re-narrow rows by the current filters and rebuild the exact preview option. */
  private rebuild(filters?: any): void {
    const chart = this.ensureChart();
    const meta = this.chartMeta();
    if (!chart || !meta || !this.builder) return;

    const ssMeta = this.data?.selfService as SelfServiceWidgetMeta | undefined;
    const rows = ssMeta
      ? filterSelfServiceRows(ssMeta, this.toSelfServiceFilters(filters))
      : ((this.data?.selfService?.rawRows as Array<Record<string, unknown>>) || []);

    const dataset = this.buildDataset(ssMeta, rows);

    this.builder.shapeOptions = meta.shapeOptions || null;
    let option = this.builder.buildChartOption(
      meta.chartType,
      meta.mapping as any,
      meta.aggregation as any,
      dataset,
      meta.style,
      meta.seriesField || '',
      meta.titleText || '',
    );

    // Preserve legend selections the user toggled across filter-driven rebuilds.
    const existingOpt = chart.getOption() as any;
    const existingLegend = Array.isArray(existingOpt?.legend) ? existingOpt.legend[0] : existingOpt?.legend;
    const savedSelected = existingLegend?.selected as Record<string, boolean> | undefined;
    if (savedSelected && Object.keys(savedSelected).length) {
      const newLegend = Array.isArray((option as any).legend) ? (option as any).legend[0] : (option as any).legend;
      const merged = { ...(newLegend || {}), selected: savedSelected };
      (option as any) = {
        ...(option as any),
        legend: Array.isArray((option as any).legend) ? [merged, ...(option as any).legend.slice(1)] : merged,
      };
    }

    this.allCategories = this.extractCategories(option as any);
    chart.setOption(option as any, true);
    chart.resize();
    this.wrapper.afterRender();

    // The wrapper registers a legendselectchanged handler that reverts native
    // ECharts legend toggles when enableLegendClick is not set. Self-service
    // charts use ECharts' built-in show/hide behaviour — remove the blocker.
    chart.off('legendselectchanged');
  }

  private toSelfServiceFilters(filters: any): SelfServiceFilters | undefined {
    if (!filters) return undefined;
    return {
      requestParams: filters.requestParams,
      queryParams: filters.queryParams,
      sourceWidgetId: filters.sourceWidgetId ?? null,
    };
  }

  /** Reconstruct the UploadedDataset the builder expects from the widget's
   *  column metadata + the (filtered) raw rows. Cell values are stringified to
   *  match the builder preview's dataset shape (Record<string,string>). */
  private buildDataset(
    ssMeta: SelfServiceWidgetMeta | undefined,
    rows: Array<Record<string, unknown>>,
  ): UploadedDataset {
    const columns: DatasetColumn[] = (ssMeta?.columns || []).map((c: any) => ({
      name: c.name,
      type: (c.type as DatasetColumn['type']) || 'string',
    }));
    const stringRows = rows.map((row) => {
      const out: Record<string, string> = {};
      for (const key of Object.keys(row)) {
        const v = row[key];
        out[key] = v === null || v === undefined ? '' : String(v);
      }
      return out;
    });
    return {
      fileName: 'published',
      sourceType: 'entity',
      columns,
      rows: stringRows,
    };
  }

  private extractCategories(option: any): string[] {
    const axis = Array.isArray(option?.xAxis) ? option.xAxis[0] : option?.xAxis;
    if (Array.isArray(axis?.data)) return axis.data.map((v: any) => String(v));
    const series = Array.isArray(option?.series) ? option.series[0] : option?.series;
    if (Array.isArray(series?.data)) {
      return series.data.map((d: any) => String(d?.name ?? d)).filter(Boolean);
    }
    return [];
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.chart?.dispose();
  }
}
